import { Camera, ClipboardList, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IncidentList, Nodata, PanelHeader, SOSModal, useToast } from '@/components';
import { RespondersList } from '@/components/shared/RespondersList/RespondersList';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable/resizable';
import { useERSContext, useMapContext } from '@/context';
import { constants } from '@/lib/constants/constants';
import { Chat } from '@/lib/modules/chat/Chat';
import { capitalizeWords, getLastMessage, getStoredSizes } from '@/lib/utils';
import MapView from '../mapview/MapView';
import { IncidentTableSkeleton } from '@/components/shared/loader';
import FloatingVideoPlayer from '@/components/ui/FloatingVideoPlayer';
import { StatusTabs } from '@/components';
import MapTreeList from '@/components/shared/MapTreeList/MapTreeList';
import CamerasList from '@/components/shared/CamerasList/CamerasList';

export const Dashboard = () => {
  const {
    users,
    cameraPopups,
    setCameraPopups,
    incidents,
    unreadInfo,
    chatHistory,
    currentSOS,
    setCurrentSOS,
    setSelectedChatId, // Use this from context instead of local state
    incomingVideoStreams,
    setIncomingVideoStreams,
  } = useERSContext();
  const { t } = useTranslation();

  const {
    selectedIncident,
    focusIncident,
    focusResponder,
    isMapLoaded,
    selectedResponder,
    unFocusResponder,
    isMapHidden,
  } = useMapContext();

  // Initialize sizes from storage or defaults
  const [panelSizes, setPanelSizes] = useState(getStoredSizes);

  const responders = useMemo(() => users.filter(user => user.permission === 'MobileUnit'), [users]);

  const [incidentStatusForChat, setIncidentStatusForChat] = useState();
  const { showToast } = useToast();
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);
  const prevIncidentsRef = useRef(null);
  const [activeTab, setActiveTab] = useState('incidents');
  const [activeRightTab, setActiveRightTab] = useState('responders');

  const minimizedMapHeightPercent = (60 / window.innerHeight) * 100;
  const mapPanelRef = useRef(null);
  useEffect(() => {
    if (isMapHidden) {
      mapPanelRef.current?.resize(minimizedMapHeightPercent);
    } else {
      mapPanelRef.current?.resize(panelSizes.rightSidebarPan.mapPan);
    }
  }, [isMapHidden]);

  useEffect(() => {
    //YIHUI: I comment out this logic that will auto expand the last incident created by the login user.
    //I do not feel this is a reasonable feature. Even it is needed, should be use a way to highlight it such as blink, but not directly expand the incident.
    /*
    if (prevIncidentsRef.current) {
      const prevIds = prevIncidentsRef.current.map(i => i.id);
      const newIncidents = incidents.filter(inc => !prevIds.includes(inc.id));
      if (newIncidents.length > 0) {
        const createdByUser = newIncidents.filter(inc => inc?.created_by?.user_id === selfProfileRef.current?.userId);
 
        if (createdByUser.length > 0) {
          focusIncident(createdByUser[0]);
        }
      }
    }
    */

    prevIncidentsRef.current = incidents;
  }, [incidents]);

  const updateSizes = (panel, newValues) => {
    setPanelSizes(prevSizes => {
      const updatedSizes = {
        ...prevSizes,
        [panel]: { ...prevSizes[panel], ...newValues },
      };

      // Save to sessionStorage
      try {
        sessionStorage.setItem(constants.SIZES_SESSION_KEY, JSON.stringify(updatedSizes));
      } catch (e) {
        console.error('Failed to save sizes to sessionStorage:', e);
      }

      return updatedSizes;
    });
  };

  const handleRowClick = data => {
    setIncidentStatusForChat(data);

    if (data?.type === 'user') {
      focusResponder(data);
    } else {
      focusIncident(data);
    }
  };

  const handleAssist = incidentId => {
    const incident = incidents.find(inc => inc.id === incidentId || inc.incidentId === incidentId);

    if (incident) {
      handleRowClick(incident);
      setCurrentSOS(null);
      focusIncident(incident);
    } else {
      setSelectedChatId(incidentId);
      setCurrentSOS(null);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let selectedResponders = selectedResponder;
    if (isMapLoaded == true) {
      handleRowClick(selectedIncident);
      unFocusResponder();
      setTimeout(() => {
        focusResponder(selectedResponders);
      }, 2000);
    }
  }, [isMapLoaded]);

  useEffect(() => {
    if (!selectedIncident) return;
    handleRowClick(selectedIncident);
  }, [selectedIncident]);

  useEffect(() => {
    if (!unreadInfo || Object.keys(unreadInfo).length === 0) return;

    const entries = Object.entries(unreadInfo);

    const latestEntry = entries.reduce((latest, current) => {
      const currentTimestamp = current[1]?.timestamp || 0;
      const latestTimestamp = latest[1]?.timestamp || 0;
      return currentTimestamp > latestTimestamp ? current : latest;
    }, entries[0]);

    const latestId = latestEntry[0];
    const latestData = latestEntry[1];

    if (!latestData?.count || latestData.count === 0) return;

    const msgObject = chatHistory[latestId];
    const lastMsg = getLastMessage(msgObject);
    if (lastMsg?.displayName !== undefined) {
      let title;
      if (msgObject?.chatBox?.isGroup) {
        title = lastMsg?.displayName;
      } else {
        // Resolve display name from contacts list to avoid showing raw user ID
        // (nickname field from SDK can be unreliable in some browsers like Edge)
        const contact = users.find(u => u.id === latestId || u.id?.split('@')[0] === latestId?.split('@')[0]);
        title = contact?.displayName || lastMsg?.displayName || lastMsg?.nickname;
      }
      showToast({
        type: constants?.DASHBOARD?.PROFILE,
        prefix: constants?.DASHBOARD?.NEW_MESSAGE_FROM,
        title,
        description: lastMsg?.body,
        profileId: msgObject?.chatBox?.jid,
      });
    }
  }, [unreadInfo]);

  const tabs = [
    { key: 'incidents', label: capitalizeWords(t('DASHBOARD.INCIDENTS')) },
    { key: 'gisFeatures', label: 'Map Features' },
  ];
  const handleTabChange = tabKey => {
    setActiveTab(tabKey);
  };

  const mapContextContent = {
    ...useERSContext(),
    ...useMapContext(),
  };

  return (
    <div className="flex w-full" role="main">
      <ResizablePanelGroup
        className="flex flex-row flex-1 h-full border border-t-0"
        direction="horizontal"
        aria-label="Dashboard layout"
      >
        <ResizablePanel
          defaultSize={panelSizes.leftSidebarPan.value}
          minSize={constants.DASHBOARD.DEFAULT_SIZES.leftSidebarPan.value}
          onResize={value => updateSizes('leftSidebarPan', { value })}
          aria-label="Incidents and responders panel"
        >
          <ResizablePanelGroup
            className="flex flex-row flex-1 h-full"
            direction="horizontal"
            aria-label="Dashboard layout"
          >
            <ResizablePanel
              defaultSize={panelSizes.leftSidebarPan.incidentPan}
              minSize={constants.DASHBOARD.DEFAULT_SIZES.leftSidebarPan.incidentPan / 1.5}
              onResize={value => updateSizes('leftSidebarPan', { incidentPan: value })}
              aria-label="Incidents panel"
            >
              <div className="flex flex-col h-full">
                {/* Main tabs - PanelHeader style */}
                <div className="flex flex-row">
                  {tabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={`px-4 py-2 font-medium shrink-0 whitespace-nowrap flex items-center gap-2 text-sm ${
                        activeTab === tab.key ? 'bg border-r border-border' : ''
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                  <div className="w-full bg-subtle border-b border-border" />
                </div>

                <div className={`flex-1 overflow-hidden ${activeTab !== 'incidents' ? 'hidden' : 'flex flex-col'}`}>
                  {showSkeleton ? (
                    <IncidentTableSkeleton />
                  ) : (
                    <IncidentList
                      incidents={incidents}
                      responders={responders}
                      onRowClick={handleRowClick}
                      onExpandedChange={setExpandedIncidentId}
                      aria-label={`${incidents.length} incidents`}
                    />
                  )}
                </div>

                <div className={`flex-1 overflow-hidden ${activeTab !== 'gisFeatures' ? 'hidden' : 'flex flex-col'}`}>
                  <MapTreeList />
                </div>
              </div>
            </ResizablePanel>
            <ResizableHandle aria-label="Resize incidents and responders panels" />
            <ResizablePanel
              defaultSize={panelSizes.leftSidebarPan.responsePan}
              minSize={constants.DASHBOARD.DEFAULT_SIZES.leftSidebarPan.responsePan}
              onResize={value => updateSizes('leftSidebarPan', { responsePan: value })}
            >
              <div className="flex flex-col h-full">
                {/* Icon tabs for Responders/Cameras */}
                <div className="flex flex-row">
                  <button
                    onClick={() => setActiveRightTab('responders')}
                    className={`px-4 py-2 flex items-center justify-center ${
                      activeRightTab === 'responders' ? 'bg border-r border-border' : ''
                    }`}
                    aria-label="Responders"
                  >
                    <Users size={20} />
                  </button>
                  <button
                    onClick={() => setActiveRightTab('cameras')}
                    className={`px-4 py-2 flex items-center justify-center ${
                      activeRightTab === 'cameras' ? 'bg border-r border-border' : ''
                    }`}
                    aria-label="Cameras"
                  >
                    <Camera size={20} />
                  </button>
                  <div className="w-full bg-subtle border-b border-border" />
                </div>
                <div className={`flex-1 bg overflow-hidden ${activeRightTab !== 'responders' ? 'hidden' : ''}`}>
                  <RespondersList
                    users={responders}
                    onRowClick={handleRowClick}
                    aria-label={`${responders?.length || 0} responders`}
                  />
                </div>
                <div className={`flex-1 bg overflow-hidden ${activeRightTab !== 'cameras' ? 'hidden' : ''}`}>
                  <CamerasList />
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
        <ResizableHandle aria-label="Resize incident and map panels" />
        <ResizablePanel
          defaultSize={panelSizes.rightSidebarPan.value}
          minSize={constants.DASHBOARD.DEFAULT_SIZES.rightSidebarPan.value / 1.5}
          onResize={value => updateSizes('rightSidebarPan', { value })}
          aria-label="Map and chat panel"
        >
          <ResizablePanelGroup
            className="flex flex-row flex-1 h-full"
            direction="vertical"
            aria-label="Map and chat layout"
          >
            <ResizablePanel
              ref={mapPanelRef}
              defaultSize={panelSizes.rightSidebarPan.mapPan}
              minSize={
                isMapHidden ? minimizedMapHeightPercent : constants.DASHBOARD.DEFAULT_SIZES.rightSidebarPan.mapPan / 1.5
              }
              maxSize={isMapHidden ? minimizedMapHeightPercent : 100}
              onResize={value => {
                if (!isMapHidden) updateSizes('rightSidebarPan', { mapPan: value });
              }}
              aria-label="Map panel"
            >
              <MapView allContextContent={mapContextContent} isPopOut={false} />
            </ResizablePanel>
            <ResizableHandle aria-label="Resize map and chat panels" />
            <ResizablePanel
              defaultSize={panelSizes.rightSidebarPan.chatPan}
              minSize={constants.DASHBOARD.DEFAULT_SIZES.rightSidebarPan.chatPan / 1.5}
              onResize={size => updateSizes('rightSidebarPan', { chatPan: size })}
              aria-label="Chat panel"
            >
              <section className="flex flex-col h-full">
                <PanelHeader title={capitalizeWords(t('DASHBOARD.CHAT'))} as="h2" />
                <div className="flex-1 overflow-hidden bg" role="region" aria-label="Chat messages">
                  {/* No need to pass selectedChatId - it's in context now */}
                  <Chat title={capitalizeWords(t('DASHBOARD.CHAT'))} />
                </div>
              </section>
            </ResizablePanel>
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>

      {currentSOS && (
        <SOSModal
          isVisible={!!currentSOS}
          sosData={currentSOS}
          onClose={() => setCurrentSOS(null)}
          onAssist={() => handleAssist(currentSOS.incidentId)}
          user={{ name: currentSOS.userName }}
          location={currentSOS.location}
          incident={currentSOS.incident}
          lastStatus={currentSOS.status}
        />
      )}

      {incomingVideoStreams.map(({ contactId, stream, completed }, idx) => {
        return (
          stream &&
          !completed && (
            <FloatingVideoPlayer
              key={`${contactId}`}
              contactId={contactId}
              stream={stream}
              onClose={() => {
                setIncomingVideoStreams(prev =>
                  prev.map(item => (item.contactId === contactId ? { ...item, completed: true } : item))
                );
              }}
            />
          )
        );
      })}

      {cameraPopups.map(popup => (
        <FloatingVideoPlayer
          key={popup.id}
          displayName={popup.displayName}
          lng={popup.lng}
          lat={popup.lat}
          url={popup.url}
          onClose={() => setCameraPopups(prev => prev.filter(p => p.id !== popup.id))}
        />
      ))}
    </div>
  );
};
